/**
 * Daily Position Report (DPR) — standalone types.
 * Kept in its own file so we never touch the shared _models.ts.
 */

/* ──────────────────────────── Activity row ──────────────────────────── */
export interface DprActivity {
  id: number
  localTimeFrom: string   // e.g. "0000"
  localTimeTo: string     // e.g. "0948"
  description: string
  position: string        // Lat/Lon or free-text
}

/* ──────────────────────────── Activity Summary ──────────────────────── */
export interface DprActivitySummary {
  speedsUpto6: string     // hh:mm
  speedsAbove6: string
  inDpMode: string
  atAnchorageBerth: string
  downTime: string
  othersSpecify: string
  othersLabel: string
  total: string           // hh:mm (auto-calculated ideally)
  fuelConsumedIn24Hrs: string
}

/* ──────────────────────────── ROB Status row ─────────────────────────── */
export interface DprRobRow {
  commodity: string
  openingStock: string
  received: string
  deliveredToOtherVessel: string
  deliveredToPlatforms: string
  consumedByVessel: string
  closingStock: string
}

/* ───────────────────────── Lube Oil Status row ─────────────────────── */
export interface DprLubeOilStatusRow {
  machinery: string
  openingStock: string
  received: string
  consumedByVessel: string
  closingStock: string
}

/* ──────────────────────────── Weather row ─────────────────────────────── */
export interface DprWeatherRow {
  localTime: string       // "0600" | "1200" | "1800" | "2400"
  windForce: string
  windSpd: string
  direction: string
  seaSwellHt: string
  swellDirection: string
  currentRate: string
  currentDirection: string
  baro: string
  sky: string
  vis: string
  temp: string
}

export interface DprTides {
  amLw: string
  amHw: string
  pmLw: string
  pmHw: string
  amLwHeight: string
  amHwHeight: string
  pmLwHeight: string
  pmHwHeight: string
  sunrise: string
  sunset: string
}

/* ──────────────────────────── Downtime row ────────────────────────────── */
export interface DprDowntimeRow {
  id: number
  from: string
  to: string
  today: string
  previous: string
  total: string           // dd:hh:mm
  reasonsRemarks: string
}

/* ──────────────────────────── P.O.B row ───────────────────────────────── */
export interface DprPobRow {
  sNo: number
  company: string
  previous: string
  paxIn: string
  paxOut: string
  total: string
  mealsBreakfast: string
  mealsLunch: string
  mealsDinner: string
  remarks: string
}

/* ──────────────────────── Statistics & Safety ──────────────────────────── */
export interface DprSafetyRow {
  particulars: string
  previousDay: string
  today: string
  cumulative: string
}

export interface DprEquipmentRow {
  equipment: string
  standbyYN: string       // "Y" | "N"
}

/* ──────────────────────── Passenger Details ──────────────────────────── */
export interface DprPassengerRow {
  srNo: number
  name: string
  company: string
  position: string
  doj: string
}

/* ──────────────────────── Engine Temperature ──────────────────────────── */
export interface DprEngineParamRow {
  label: string
  loPres: string
  jcwCooler: string
  loTempCooler: string
}

export interface DprExhTempRow {
  label: string
  unit1: string
  unit2: string
  unit3: string
  unit4: string
  unit5: string
  unit6: string
  unit7: string
  unit8: string
}

export interface DprEngineRunningHoursRow {
  label: string
  openingHours: string
  dailyHours: string
  totalHours: string
}

export interface DprEngineTemp {
  params: DprEngineParamRow[]
  exhTemps: DprExhTempRow[]
}

/* ──────────────────────── Movement Report ──────────────────────────── */
export interface DprMovementReportRow {
  berShifting: string
  pilotName: string
  startedBothME: string
  cOff: string
  makeFastPushStart: string
  lineCastOffPushStop: string
  pilotRelease: string
  alongSideFinishedME: string
  totalRHrs: string
  otherTug: string
  remarks: string
  // Cross-DPR tracking flags
  isContinue?: boolean
  isResolved?: boolean
  resolvedByDprId?: number | null
  // Context from previous DPR (populated on frontend, not saved)
  previousContinueStartTime?: string
  previousContinueDprDate?: string
}

/* ──────────────────────── FO Consumption ──────────────────────────── */
export interface DprFoConsumptionRow {
  date: string
  above6Hrs: string
  above6Cons: string
  steamingHrs: string
  steamingCons: string
  platformDpHrs: string
  platformDpCons: string
  atAnchorageHrs: string
  atAnchorageCons: string
  atBerthHrs: string
  atBerthCons: string
  totalFoCons: string
  totalHr1: string
  totalHr2: string
  remarks: string
}

export interface DprMachineryFoConsumptionRow {
  machinery: string
  foConsumption: string
}

/* ──────────────────────────── Full DPR record ──────────────────────── */
export interface DailyPositionReportRecord {
  id: number
  vesselId?: number
  createdByUserId?: number
  reportDate: string      // ISO yyyy-MM-dd
  vesselName: string
  voyageNo: string
  portOrSea: string       // 'PORT' | 'SEA'
  fromLocation: string
  toLocation: string
  eta: string
  draftFwd: string
  draftAft: string
  weather: string
  wind: string
  seaState: string
  // Tab 0 — FO Consumption
  foConsumptionRows: DprFoConsumptionRow[]
  machineryFoConsumptionRows: DprMachineryFoConsumptionRow[]
  // Date & Location
  dlDate: string
  dlTime: string
  dlLocation: string
  dlPosition: string
  activities: DprActivity[]
  activitySummary: DprActivitySummary
  // Tab 4 — ROB Status
  robStatus: DprRobRow[]
  lubeOilStatusRows: DprLubeOilStatusRow[]
  // Tab 5 — Weather
  weatherRows: DprWeatherRow[]
  tides: DprTides
  // Tab 6 — Downtime
  downtimeRows: DprDowntimeRow[]
  // Tab 7 — P.O.B
  pobRows: DprPobRow[]
  // Tab 8 — Statistics & Safety
  safetyLeft: DprSafetyRow[]
  safetyRight: DprSafetyRow[]
  equipmentRows: DprEquipmentRow[]
  passengerRows: DprPassengerRow[]
  engineTemp: DprEngineTemp
  engineRunningHours: DprEngineRunningHoursRow[]
  // Tab — Movement Report (single record per DPR)
  movementReport?: DprMovementReportRow

  status: 'DRAFT' | 'SUBMITTED' | 'REVIEWED' | 'REJECTED'
  createdByRank?: string
  createdAt: string
}

/* ────────────────────── Blank defaults (for forms) ────────────────── */
let _nextId = 1
export const newDprId = () => _nextId++

export const blankActivity = (): DprActivity => ({
  id: Date.now(),
  localTimeFrom: '',
  localTimeTo: '',
  description: '',
  position: '',
})

export const blankFoConsumptionRow = (): DprFoConsumptionRow => {
  const d = new Date()
  const todayStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
  return {
    date: todayStr,
    above6Hrs: '',
  above6Cons: '',
  steamingHrs: '',
  steamingCons: '',
  platformDpHrs: '',
  platformDpCons: '',
  atAnchorageHrs: '',
  atAnchorageCons: '',
  atBerthHrs: '',
  atBerthCons: '',
  totalFoCons: '',
  totalHr1: '',
  totalHr2: '',
  remarks: '',
}
}

export const blankSummary = (): DprActivitySummary => ({
  speedsUpto6: '',
  speedsAbove6: '',
  inDpMode: '',
  atAnchorageBerth: '',
  downTime: '',
  othersSpecify: '',
  othersLabel: '',
  total: '',
  fuelConsumedIn24Hrs: '',
})

export const blankMachineryFoConsumptionRow = (): DprMachineryFoConsumptionRow => ({
  machinery: '',
  foConsumption: '',
})

export const DEFAULT_ROB_ROWS: DprRobRow[] = [
  { commodity: 'Fuel (KL)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'Fresh Water', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'Blended Cement (MT)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'Barite (MT)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'Base Oil (Barrels)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'OBM (Barrels)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
  { commodity: 'OSD (Ltrs)', openingStock: '0.0', received: '0.0', deliveredToOtherVessel: '0.0', deliveredToPlatforms: '0.0', consumedByVessel: '0.0', closingStock: '0.0' },
]

export const DEFAULT_WEATHER_ROWS: DprWeatherRow[] = [
  { localTime: '0600', windForce: '', windSpd: '', direction: '', seaSwellHt: '', swellDirection: '', currentRate: '', currentDirection: '', baro: '', sky: '', vis: '', temp: '' },
  { localTime: '1200', windForce: '', windSpd: '', direction: '', seaSwellHt: '', swellDirection: '', currentRate: '', currentDirection: '', baro: '', sky: '', vis: '', temp: '' },
  { localTime: '1800', windForce: '', windSpd: '', direction: '', seaSwellHt: '', swellDirection: '', currentRate: '', currentDirection: '', baro: '', sky: '', vis: '', temp: '' },
  { localTime: '2400', windForce: '', windSpd: '', direction: '', seaSwellHt: '', swellDirection: '', currentRate: '', currentDirection: '', baro: '', sky: '', vis: '', temp: '' },
]

export const blankTides = (): DprTides => ({
  amLw: '', amHw: '', pmLw: '', pmHw: '',
  amLwHeight: '', amHwHeight: '', pmLwHeight: '', pmHwHeight: '',
  sunrise: '', sunset: '',
})

export const blankDowntimeRow = (): DprDowntimeRow => ({
  id: Date.now(),
  from: '',
  to: '',
  today: '',
  previous: '',
  total: '',
  reasonsRemarks: '',
})

export const blankLubeOilStatusRow = (): DprLubeOilStatusRow => ({
  machinery: '',
  openingStock: '',
  received: '',
  consumedByVessel: '',
  closingStock: '',
})

export const DEFAULT_POB_ROWS: DprPobRow[] = [
  { sNo: 1, company: 'Crew', previous: '0', paxIn: '0', paxOut: '0', total: '0', mealsBreakfast: '0', mealsLunch: '0', mealsDinner: '0', remarks: '' },
  { sNo: 2, company: 'CIL', previous: '0', paxIn: '0', paxOut: '0', total: '0', mealsBreakfast: '0', mealsLunch: '0', mealsDinner: '0', remarks: '' },
  { sNo: 3, company: 'Diving', previous: '0', paxIn: '0', paxOut: '0', total: '0', mealsBreakfast: '0', mealsLunch: '0', mealsDinner: '0', remarks: '' },
  { sNo: 4, company: 'OTHERS', previous: '0', paxIn: '0', paxOut: '0', total: '0', mealsBreakfast: '0', mealsLunch: '0', mealsDinner: '0', remarks: '' },
]

export const DEFAULT_SAFETY_LEFT: DprSafetyRow[] = [
  { particulars: 'Reportable Incidents', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Medical Treatment Cases', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Near Miss Incidents', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Days lost due to LTI', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Days since last LTI', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Drills Carried Out', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Mandays since last LTI', previousDay: '0', today: '0', cumulative: '0' },
]

export const DEFAULT_SAFETY_RIGHT: DprSafetyRow[] = [
  { particulars: 'Tool Box Meetings', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Safety Meetings', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Safety Inspections', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Hygiene Inspections', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'HSE Audits - Internal', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'HSE Audits - External', previousDay: '0', today: '0', cumulative: '0' },
  { particulars: 'Permits Issued', previousDay: '0', today: '0', cumulative: '0' },
]

export const blankPassengerRow = (): DprPassengerRow => ({
  srNo: 0,
  name: '',
  company: '',
  position: '',
  doj: '',
})

export const DEFAULT_EQUIPMENT_ROWS: DprEquipmentRow[] = [
  { equipment: 'Fi-Fi Equip', standbyYN: 'Y' },
  { equipment: 'Rescue Boat', standbyYN: 'Y' },
  { equipment: 'Nav Equip', standbyYN: 'Y' },
  { equipment: 'Main Engines', standbyYN: 'Y' },
  { equipment: 'Thrusters', standbyYN: 'Y' },
  { equipment: 'Towing Winch', standbyYN: 'Y' },
  { equipment: 'Cargo Pumps', standbyYN: 'Y' },
  { equipment: 'Oil Spill Equip', standbyYN: 'Y' },
  { equipment: 'Steering', standbyYN: 'Y' },
]

export const normalizeEquipmentRows = (
  rows?: DprEquipmentRow[] | null
): DprEquipmentRow[] => {
  const existingRows = Array.isArray(rows) ? rows : []
  const defaultEquipmentKeys = new Set(
    DEFAULT_EQUIPMENT_ROWS.map((row) => row.equipment.trim().toLowerCase())
  )
  const byEquipment = new Map(
    existingRows.map((row) => [row.equipment.trim().toLowerCase(), row])
  )

  const normalizedDefaults = DEFAULT_EQUIPMENT_ROWS.map((row) => {
    const existing = byEquipment.get(row.equipment.trim().toLowerCase())
    return existing ? {...row, standbyYN: existing.standbyYN ?? row.standbyYN} : {...row}
  })

  const extraRows = existingRows.filter(
    (row) => !defaultEquipmentKeys.has(row.equipment.trim().toLowerCase())
  )

  return [...normalizedDefaults, ...extraRows.map((row) => ({...row}))]
}

export const DEFAULT_ENGINE_PARAMS: DprEngineParamRow[] = [
  { label: 'M/E( P)', loPres: '', jcwCooler: '', loTempCooler: '' },
  { label: 'M/E( S)', loPres: '', jcwCooler: '', loTempCooler: '' },
  { label: 'AUX (1)', loPres: '', jcwCooler: '', loTempCooler: '' },
  { label: 'AUX (2)', loPres: '', jcwCooler: '', loTempCooler: '' },
]

export const DEFAULT_EXH_TEMPS: DprExhTempRow[] = [
  { label: 'M/E(P)', unit1: '', unit2: '', unit3: '', unit4: '', unit5: '', unit6: '', unit7: '', unit8: '' },
  { label: 'M/E(S)', unit1: '', unit2: '', unit3: '', unit4: '', unit5: '', unit6: '', unit7: '', unit8: '' },
  { label: 'AUX(1)', unit1: '', unit2: '', unit3: '', unit4: '', unit5: '', unit6: '', unit7: '', unit8: '' },
  { label: 'AUX(2)', unit1: '', unit2: '', unit3: '', unit4: '', unit5: '', unit6: '', unit7: '', unit8: '' },
]

export const DEFAULT_ENGINE_RUNNING_HOURS: DprEngineRunningHoursRow[] = [
  { label: 'M/E( P)', openingHours: '0', dailyHours: '0', totalHours: '0' },
  { label: 'M/E( S)', openingHours: '0', dailyHours: '0', totalHours: '0' },
  { label: 'AUX (1)', openingHours: '0', dailyHours: '0', totalHours: '0' },
  { label: 'AUX (2)', openingHours: '0', dailyHours: '0', totalHours: '0' },
]

export const blankMovementReportRow = (): DprMovementReportRow => ({
  berShifting: '',
  pilotName: '',
  startedBothME: '',
  cOff: '',
  makeFastPushStart: '',
  lineCastOffPushStop: '',
  pilotRelease: '',
  alongSideFinishedME: '',
  totalRHrs: '',
  otherTug: '',
  remarks: '',
})

export const blankDpr = (): DailyPositionReportRecord => ({
  id: 0,
  reportDate: '',
  vesselName: '',
  voyageNo: '',
  portOrSea: 'SEA',
  fromLocation: '',
  toLocation: '',
  eta: '',
  draftFwd: '',
  draftAft: '',
  weather: '',
  wind: '',
  seaState: '',
  foConsumptionRows: [blankFoConsumptionRow()],
  machineryFoConsumptionRows: [blankMachineryFoConsumptionRow()],
  dlDate: '',
  dlTime: '',
  dlLocation: '',
  dlPosition: '',
  activities: [blankActivity()],
  activitySummary: blankSummary(),
  robStatus: DEFAULT_ROB_ROWS.map((r) => ({ ...r })),
  lubeOilStatusRows: [blankLubeOilStatusRow()],
  weatherRows: DEFAULT_WEATHER_ROWS.map((r) => ({ ...r })),
  tides: blankTides(),
  downtimeRows: [blankDowntimeRow()],
  pobRows: DEFAULT_POB_ROWS.map((r) => ({ ...r })),
  safetyLeft: DEFAULT_SAFETY_LEFT.map((r) => ({ ...r })),
  safetyRight: DEFAULT_SAFETY_RIGHT.map((r) => ({ ...r })),
  equipmentRows: DEFAULT_EQUIPMENT_ROWS.map((r) => ({ ...r })),
  passengerRows: [],
  engineTemp: {
    params: DEFAULT_ENGINE_PARAMS.map((r) => ({ ...r })),
    exhTemps: DEFAULT_EXH_TEMPS.map((r) => ({ ...r })),
  },
  engineRunningHours: DEFAULT_ENGINE_RUNNING_HOURS.map((r) => ({ ...r })),
  status: 'DRAFT',
  createdAt: new Date().toISOString(),
})
